// src/components/SectionHeader.jsx
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import clsx from "clsx";

const API_BASE = process.env.REACT_APP_API_BASE || "http://192.168.1.89:8000/api";

/* ------------------------------ helpers ------------------------------ */

const bsColorToVar = (accent) => {
  if (!accent) return null;
  const map = {
    primary: "var(--bs-primary)",
    secondary: "var(--bs-secondary)",
    success: "var(--bs-success)",
    danger: "var(--bs-danger)",
    warning: "var(--bs-warning)",
    info: "var(--bs-info)",
    light: "var(--bs-light)",
    dark: "var(--bs-dark)",
  };
  return map[accent] || accent;
};

const toQueryString = (params = {}) => {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === "") return;
    if (Array.isArray(v)) v.forEach((item) => q.append(k, String(item)));
    else q.append(k, String(v));
  });
  const s = q.toString();
  return s ? `?${s}` : "";
};

const getByPath = (obj, path) => {
  if (!path) return undefined;
  return path.split(".").reduce((acc, key) => (acc ? acc[key] : undefined), obj);
};

const normalizeList = (data) =>
  Array.isArray(data?.results) ? data.results : Array.isArray(data) ? data : [];

/* ----------------------------- component ----------------------------- */

/**
 * Dynamic, API‑aware section header.
 *
 * New category-centric props:
 * - categorySlug: string                       -> when provided, we can auto fetch the category
 * - autoFetchCategory: boolean (default false) -> fetch /categories/:slug/ to get title/description
 * - categoryTitleField: string (default "name")
 * - categorySubtitleField: string (default "description")
 * - autoCountCategoryArticles: boolean         -> count /categories/:slug/articles/
 *
 * You can still pass a manual `title`, `subtitle`, `count`, etc. and they will
 * override the fetched values.
 */
const SectionHeader = ({
  // display
  title,
  subtitle,
  description,
  count,
  badge,
  icon,
  accent = "primary",
  align = "left",
  underline = true,
  border = false,
  compact = false,
  pill = false,
  right = null,
  className = "",
  titleClassName = "mb-0 fw-semibold", // <- you can change the typography from outside
  onViewAll,
  viewAllText = "View all",

  // counting (generic)
  fetchCount = false,
  countEndpoint,
  countMethod = "get",
  countParams = {},
  countField, // e.g. "count"
  transformCount,
  autoBadgeFromCount = false,

  // category‑aware options
  categorySlug,
  autoFetchCategory = false,
  categoryTitleField = "name",
  categorySubtitleField = "description",
  autoCountCategoryArticles = false,
}) => {
  const [remoteCount, setRemoteCount] = useState(null);
  const [countLoading, setCountLoading] = useState(false);
  const [countErr, setCountErr] = useState(null);

  const [cat, setCat] = useState(null);
  const [catLoading, setCatLoading] = useState(false);
  const [catErr, setCatErr] = useState(null);

  /* -------------------- fetch category (name/description) -------------------- */
  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    async function run() {
      if (!autoFetchCategory || !categorySlug) return;
      setCatLoading(true);
      setCatErr(null);
      try {
        const url = `${API_BASE}/categories/${categorySlug}/`;
        const res = await axios.get(url, { signal: controller.signal });
        if (cancelled) return;
        setCat(res.data ?? null);
      } catch (e) {
        if (!cancelled) setCatErr(e);
      } finally {
        if (!cancelled) setCatLoading(false);
      }
    }
    run();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [autoFetchCategory, categorySlug]);

  /* ---------------------------- fetch count (generic or category) ---------------------------- */
  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    async function run() {
      // If caller set fetchCount explicitly, use provided endpoint.
      // Else, if autoCountCategoryArticles and a slug exist, use /categories/:slug/articles/
      const shouldAutoCategory =
        !fetchCount && autoCountCategoryArticles && categorySlug;

      const doFetch = fetchCount || shouldAutoCategory;
      if (!doFetch) return;

      const endpoint = shouldAutoCategory
        ? `/categories/${categorySlug}/articles/`
        : countEndpoint;

      if (!endpoint) return;

      setCountLoading(true);
      setCountErr(null);

      try {
        const url =
          (endpoint.startsWith("http")
            ? endpoint
            : `${API_BASE}/${endpoint.replace(/^\/+/, "")}`) +
          toQueryString(countParams);

        const method = fetchCount ? countMethod : "get";

        const res = await axios.request({
          url,
          method,
          signal: controller.signal,
        });

        if (cancelled) return;

        let n;
        if (countField) {
          n = getByPath(res.data, countField);
        }
        if (typeof n !== "number") {
          const list = normalizeList(res.data);
          n = Array.isArray(list) ? list.length : 0;
        }
        if (typeof transformCount === "function") {
          n = transformCount(n, res.data);
        }
        setRemoteCount(n);
      } catch (e) {
        if (!cancelled) setCountErr(e);
      } finally {
        if (!cancelled) setCountLoading(false);
      }
    }
    run();

    // Note: JSON.stringify for countParams to correctly track changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fetchCount,
    countEndpoint,
    countMethod,
    categorySlug,
    autoCountCategoryArticles,
    JSON.stringify(countParams),
    countField,
    transformCount,
  ]);

  /* ------------------------------- computed UI values ------------------------------- */

  const finalTitle = useMemo(() => {
    if (title) return title;
    if (cat && autoFetchCategory) {
      const v = getByPath(cat, categoryTitleField);
      if (v) return v;
    }
    if (categorySlug) return categorySlug; // fallback
    return null;
  }, [title, cat, autoFetchCategory, categoryTitleField, categorySlug]);

  const finalSubtitle = useMemo(() => {
    if (subtitle) return subtitle;
    if (cat && autoFetchCategory) {
      const v = getByPath(cat, categorySubtitleField);
      if (v) return v;
    }
    return null;
  }, [subtitle, cat, autoFetchCategory, categorySubtitleField]);

  const finalCount = useMemo(() => {
    if (typeof count === "number") return count;
    if (typeof remoteCount === "number") return remoteCount;
    return null;
  }, [count, remoteCount]);

  const showBadge = useMemo(() => {
    if (badge) return badge;
    if (autoBadgeFromCount && typeof finalCount === "number") {
      return String(finalCount);
    }
    return null;
  }, [badge, autoBadgeFromCount, finalCount]);

  const color = bsColorToVar(accent);
  const isCenter = align === "center";
  const isRight = align === "right";

  const wrapCls = clsx(
    "section-header",
    border && "border-bottom pb-3",
    compact ? "mb-3" : "mb-4",
    className
  );

  const titleRowCls = clsx(
    "d-flex",
    isCenter
      ? "flex-column align-items-center text-center"
      : isRight
      ? "justify-content-between text-end"
      : "justify-content-between"
  );

  const badgeCls = clsx(
    pill ? `badge rounded-pill text-bg-${accent}` : `badge text-bg-${accent}`
  );

  const viewAllBtn =
    onViewAll &&
    typeof onViewAll === "function" && (
      <button
        type="button"
        className="btn btn-outline-secondary btn-sm"
        onClick={onViewAll}
      >
        {viewAllText}
      </button>
    );

  const anyLoading = countLoading || catLoading;
  const loadingChip = anyLoading ? (
    <span className="badge text-bg-light border d-inline-flex align-items-center gap-1">
      <span
        className="spinner-border spinner-border-sm text-secondary"
        role="status"
        aria-hidden="true"
      />
      Loading
    </span>
  ) : null;

  const anyErr = countErr || catErr;
  const errorChip = anyErr ? (
    <span className="badge text-bg-danger">Error</span>
  ) : null;

  /* ----------------------------------- render ----------------------------------- */

  return (
    <header className={wrapCls} style={{ "--sh-accent": color }}>
      <div className={titleRowCls} style={{ gap: "0.75rem" }}>
        <div
          className={clsx(
            "d-flex",
            isCenter && "justify-content-center",
            isRight && "ms-auto",
            "align-items-center"
          )}
          style={{ gap: ".5rem" }}
        >
          {icon && (
            <span
              className={clsx(
                "d-inline-flex align-items-center justify-content-center rounded-3",
                `text-${accent}`
              )}
              style={{
                width: 40,
                height: 40,
                background:
                  "color-mix(in srgb, var(--sh-accent, var(--bs-primary)) 12%, transparent)",
              }}
            >
              <i className={`bi ${icon} fs-5`} />
            </span>
          )}

          <div className={clsx(isCenter && "text-center")}>
            <div className="d-flex align-items-center flex-wrap" style={{ gap: ".5rem" }}>
              {finalTitle ? (
                <h5 className={titleClassName}>{finalTitle}</h5>
              ) : (
                <h5 className={titleClassName}>Section</h5>
              )}

              {typeof finalCount === "number" && !autoBadgeFromCount && (
                <span className="ms-1 text-body-secondary small">
                  ({finalCount})
                </span>
              )}

              {showBadge && <span className={badgeCls}>{showBadge}</span>}

              {loadingChip}
              {errorChip}
            </div>

            {(finalSubtitle || description) && (
              <div className="text-body-secondary small mt-1">
                {finalSubtitle || description}
              </div>
            )}
          </div>
        </div>

        <div
          className={clsx(
            "d-flex align-items-center",
            isCenter && "justify-content-center"
          )}
          style={{ gap: ".5rem" }}
        >
          {right}
          {viewAllBtn}
        </div>
      </div>

      {underline && (
        <div
          className={clsx(isCenter ? "mx-auto" : isRight ? "ms-auto" : "")}
          style={{
            height: 3,
            width: 72,
            borderRadius: 999,
            background:
              "linear-gradient(90deg, var(--sh-accent, var(--bs-primary)), color-mix(in srgb, var(--sh-accent, var(--bs-primary)) 30%, transparent))",
            marginTop: (finalSubtitle || description) ? "0.75rem" : "0.5rem",
          }}
        />
      )}
    </header>
  );
};

export default SectionHeader;
